import { TreatmentOffer } from "@/services/commerceOffers";
import { AddCartItemPayload, Cart, CartItem } from "@/types/cart";

/** The tech spec 5.4 outcomes a chat add can end in once its add request was sent. */
export type AddOutcome = "added" | "not_added" | "unconfirmed" | "view_cart";

/** The exact one-time add item, built field by field so no extra offer field reaches the cart. */
export const buildAddToCartItem = (
  offer: TreatmentOffer
): AddCartItemPayload => ({
  item_type: "individual-treatment",
  treatment_product_id: offer.treatment_product_id,
  quantity: 1,
  is_subscription: false,
});

const isOfferLine = (offer: TreatmentOffer, line: CartItem): boolean =>
  line.treatment_product?.id === offer.treatment_product_id;

/** Whether the cart holds the offer's treatment in any mode or item type. */
export const hasOfferLine = (offer: TreatmentOffer, cart: Cart): boolean =>
  cart.line_items.some((line) => isOfferLine(offer, line));

/** One available one-time line of the item type, as a chat add creates it. */
export const isOneTimeAvailableLine = (
  line: CartItem,
  itemType: CartItem["item_type"]
): boolean =>
  line.item_type === itemType &&
  line.quantity === 1 &&
  line.is_subscription === false &&
  line.selling_plan_id === null &&
  line.unavailable === false;

/**
 * Whether the add-to-cart response (or readback) proves the offer was added.
 * The server's add overwrites an existing line for the same treatment and silently
 * skips subscribed treatments, so only an exact new line in the same active cart counts.
 *
 * Pre: cartBefore is an authoritative read taken just before the tap; a stale one can
 * confirm another tab's add. cartAfter is already validated: normalizeCart turns a
 * truncated response's missing line_items into [], which reads here as not_added.
 */
export function decideAddOutcome(
  offer: TreatmentOffer,
  cartBefore: Cart,
  cartAfter: Cart | undefined
): AddOutcome {
  // Already in the cart before the tap: an unchanged line proves nothing was added.
  if (hasOfferLine(offer, cartBefore)) return "view_cart";
  if (
    !cartBefore.id ||
    cartAfter?.id !== cartBefore.id ||
    cartAfter.status !== "active"
  )
    return "unconfirmed";
  const offerLines = cartAfter.line_items.filter((line) =>
    isOfferLine(offer, line)
  );
  // Several lines for one treatment are outside the server's contract; claim nothing.
  if (offerLines.length > 1) return "unconfirmed";
  return offerLines.length === 1 &&
    isOneTimeAvailableLine(offerLines[0], "individual-treatment")
    ? "added"
    : "not_added";
}
